import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { hashPassword } from '../utils/security';
import * as api from '../lib/api';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

const INITIAL_NOTICES = [];

const INITIAL_COURSES = [
  { id: 1, name: 'Direito', description: 'Formação voltada para legislação, advocacia, magistratura, promotoria e demais carreiras jurídicas.', image: null, status: 'Ativo', turmas: [
    { turmaId: 'A', vacancies: 20 },
    { turmaId: 'B', vacancies: 20 }
  ] },
  { id: 2, name: 'Engenharia Mecânica', description: 'Foco em projetos, manutenção e operação de sistemas mecânicos, industriais e automotivos.', image: null, status: 'Ativo', turmas: [
    { turmaId: 'A', vacancies: 20 },
    { turmaId: 'B', vacancies: 20 }
  ] },
  { id: 3, name: 'Gastronomia', description: 'Artes culinárias, gestão de restaurantes, alta gastronomia e segurança alimentar.', image: null, status: 'Ativo', turmas: [
    { turmaId: 'A', vacancies: 20 },
    { turmaId: 'B', vacancies: 20 }
  ] },
  { id: 4, name: 'Medicina', description: 'Formação médica completa com foco em ética, diagnóstico preciso e excelência clínica.', image: null, status: 'Ativo', turmas: [
    { turmaId: 'A', vacancies: 20 },
    { turmaId: 'B', vacancies: 20 }
  ] }
];

// Backward compatibility export (for any code still importing COURSE_CATALOG directly)
export const COURSE_CATALOG = INITIAL_COURSES;

const INITIAL_NOTIFICATIONS = [];

const INITIAL_CLASSES = [
  { id: 101, name: 'A', course: 'Medicina', period: 'Integral', maxStudents: 20 },
  { id: 102, name: 'B', course: 'Medicina', period: 'Integral', maxStudents: 20 },
  { id: 201, name: 'A', course: 'Gastronomia', period: 'Matutino', maxStudents: 20 },
  { id: 202, name: 'B', course: 'Gastronomia', period: 'Vespertino', maxStudents: 20 },
  { id: 301, name: 'A', course: 'Engenharia Mecânica', period: 'Noturno', maxStudents: 20 },
  { id: 302, name: 'B', course: 'Engenharia Mecânica', period: 'Noturno', maxStudents: 20 },
  { id: 401, name: 'A', course: 'Direito', period: 'Matutino', maxStudents: 20 },
  { id: 402, name: 'B', course: 'Direito', period: 'Noturno', maxStudents: 20 }
];

const DEFAULT_ASSESSMENTS = [
  { id: 1, name: 'Avaliação 01', type: 'Prova', weight: 40, date: '2026-05-15', max: 10 },
  { id: 2, name: 'Trabalho Semestral', type: 'Trabalho', weight: 20, date: '2026-05-22', max: 10 },
  { id: 3, name: 'Exame Final', type: 'Prova', weight: 40, date: '2026-06-10', max: 10 },
];

// Chave de controle do seed — incrementar se precisar re-sedar
const ADMIN_SEED_KEY = 'lumenfall_admin_seeded_v3';

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [notices, setNotices] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_notices_v2');
      return saved ? JSON.parse(saved) : INITIAL_NOTICES;
    } catch (e) {
      return INITIAL_NOTICES;
    }
  });

  const [assessments, setAssessments] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_professor_assessments');
      const parsed = saved ? JSON.parse(saved) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_ASSESSMENTS;
    } catch (e) {
      return DEFAULT_ASSESSMENTS;
    }
  });

  const [classes, setClasses] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_classes_v3');
      return saved ? JSON.parse(saved) : INITIAL_CLASSES;
    } catch (e) {
      return INITIAL_CLASSES;
    }
  });

  const [courses, setCourses] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_courses_v1');
      return saved ? JSON.parse(saved) : INITIAL_COURSES;
    } catch (e) {
      return INITIAL_COURSES;
    }
  });

  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_notifications');
      if (saved && saved !== 'undefined') {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : INITIAL_NOTIFICATIONS;
      }
      return INITIAL_NOTIFICATIONS;
    } catch (e) {
      return INITIAL_NOTIFICATIONS;
    }
  });

  const [grades, setGrades] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_grades');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [idPhotos, setIdPhotos] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_id_photos');
      const parsed = saved ? JSON.parse(saved) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  });

  const [chatMessages, setChatMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_chat_messages');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('lumenfall_notices_v2', JSON.stringify(notices));
  }, [notices]);

  useEffect(() => {
    localStorage.setItem('lumenfall_professor_assessments', JSON.stringify(assessments));
  }, [assessments]);

  useEffect(() => {
    localStorage.setItem('lumenfall_classes_v3', JSON.stringify(classes));
  }, [classes]);

  useEffect(() => {
    localStorage.setItem('lumenfall_courses_v1', JSON.stringify(courses));
  }, [courses]);

  useEffect(() => {
    localStorage.setItem('lumenfall_notifications', JSON.stringify(notifications));
  }, [notifications]);

  useEffect(() => {
    localStorage.setItem('lumenfall_grades', JSON.stringify(grades));
  }, [grades]);

  useEffect(() => {
    localStorage.setItem('lumenfall_id_photos', JSON.stringify(idPhotos));
  }, [idPhotos]);

  useEffect(() => {
    localStorage.setItem('lumenfall_chat_messages', JSON.stringify(chatMessages));
  }, [chatMessages]);

  const getConversationId = (a, b) => {
    const left = String(a || '').trim();
    const right = String(b || '').trim();
    return [left, right].sort().join('::');
  };

  const getConversationMessages = (conversationId) => {
    return (chatMessages || []).filter(m => m.conversationId === conversationId).sort((x, y) => (x.createdAt || 0) - (y.createdAt || 0));
  };

  const sendChatMessage = ({ conversationId, fromId, toId, text }) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    const now = Date.now();
    const message = {
      id: now,
      conversationId,
      fromId,
      toId,
      text: clean,
      createdAt: now
    };
    setChatMessages(prev => [...(prev || []), message]);
    addAuditLog(`Mensagem enviada (${conversationId})`, { name: 'Chat' });
  };

  const addNotice = (notice) => {
    setNotices(prev => [{ ...notice, id: Date.now(), time: 'Agora' }, ...prev]);
  };

  const removeNotice = (id) => {
    setNotices(prev => prev.filter(n => n.id !== id));
  };

  const [readNotices, setReadNotices] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_read_notices');
      if (saved && saved !== 'undefined') {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch (e) { return []; }
  });

  useEffect(() => {
    localStorage.setItem('lumenfall_read_notices', JSON.stringify(readNotices));
  }, [readNotices]);

  const markAllAsRead = (noticeIds = [], systemNotifIds = []) => {
    // 1. Notificações de sistema (Remoção física)
    if (systemNotifIds.length > 0 || (systemNotifIds.length === 0 && noticeIds.length === 0)) {
      setNotifications(prev => {
        const current = Array.isArray(prev) ? prev : [];
        if (systemNotifIds.length === 0) return []; // Limpar tudo se array vazio
        const sIds = systemNotifIds.map(id => String(id).trim());
        return current.filter(n => !sIds.includes(String(n.id).trim()));
      });
    }

    // 2. Avisos Acadêmicos (Marcação lógica)
    setReadNotices(prev => {
      const currentRead = Array.isArray(prev) ? prev : [];
      let toAdd = [];
      if (noticeIds.length === 0 && systemNotifIds.length === 0) {
        toAdd = (notices || []).map(n => String(n.id).trim());
      } else {
        toAdd = noticeIds.map(id => String(id).trim());
      }
      return [...new Set([...currentRead.map(id => String(id).trim()), ...toAdd])];
    });
  };

  const markNotificationAsRead = (id, source = 'academic') => {
    const stringId = String(id).trim();
    
    if (source === 'academic') {
      setReadNotices(prev => {
        const currentRead = Array.isArray(prev) ? prev : [];
        return [...new Set([...currentRead.map(id => String(id).trim()), stringId])];
      });
    } else {
      setNotifications(prev => {
        const current = Array.isArray(prev) ? prev : [];
        return current.filter(n => String(n.id).trim() !== stringId);
      });
    }
  };

  const updateGrade = (enrollmentId, subject, assessmentId, grade, title) => {
    setGrades(prev => {
      const existing = prev.findIndex(g => g.enrollmentId === enrollmentId && g.subject === subject && g.assessmentId === assessmentId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], grade, title, date: new Date().toISOString() };
        return updated;
      }
      return [...prev, { enrollmentId, subject, assessmentId, grade, title, date: new Date().toISOString() }];
    });
    addAuditLog(`Nota lançada para aluno ${enrollmentId}: ${grade} em ${subject}`, { name: 'Sistema Docente' });
  };

  const deleteGradesByAssessment = (subject, assessmentId) => {
    setGrades(prev => prev.filter(g => !(g.subject === subject && g.assessmentId === assessmentId)));
    addAuditLog(`Notas removidas da avaliação ${assessmentId} em ${subject}`, { name: 'Sistema Docente' });
  };

  const registerAttendance = (classId, date, attendanceMap) => {
    setAttendanceRecords(prev => {
      const existingIndex = prev.findIndex(r => r.classId === classId && r.date === date);
      
      const record = {
        id: existingIndex >= 0 ? prev[existingIndex].id : Date.now(),
        classId,
        date,
        attendance: attendanceMap // { enrollmentId: 'P' | 'F' | 'A' | 'J' }
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = record;
        return updated;
      }
      return [...prev, record];
    });
    
    addAuditLog(`Chamada ${attendanceRecords.some(r => r.classId === classId && r.date === date) ? 'atualizada' : 'realizada'} para turma ${classId} em ${date}`, { name: 'Sistema Docente' });
  };

  const updateFrequency = (classId, newFreq) => {
    setClasses(prev => prev.map(c => 
      c.id === classId ? { ...c, frequency: newFreq } : c
    ));
  };

  const [allEnrollments, setAllEnrollments] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  
  // Helper: Normalize professor status (handle legacy values like 'Ativo')
  const normalizeProfessorStatus = (status) => {
    if (!status || status === '' || status === 'Ativo') return 'approved';
    return status;
  };

  // Fetch initial data from API
  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [studentsRes, professorsRes] = await Promise.all([
        api.getStudents(),
        api.getProfessors(),
      ]);
      
      if (studentsRes.success && studentsRes.data) {
        const students = Array.isArray(studentsRes.data) ? studentsRes.data : [];
        // Add enrollmentId as id for frontend compatibility
        const processedStudents = students.map(student => ({
          ...student,
          enrollmentId: student.id,
        }));
        setAllEnrollments(processedStudents);
      }
      
      if (professorsRes.success && professorsRes.data) {
        const professors = Array.isArray(professorsRes.data) ? professorsRes.data : [];
        // Normalize statuses and add enrollmentId
        const processedProfessors = professors.map(prof => ({
          ...prof,
          enrollmentId: prof.id,
          status: normalizeProfessorStatus(prof.status),
        }));
        setProfessors(processedProfessors);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const studentEnrollments = useMemo(() => {
    if (user && user.type === 'student' && Array.isArray(allEnrollments)) {
      return allEnrollments.filter(e => 
        (e.enrollmentId && e.enrollmentId === user.enrollmentId) || 
        (e.citizenId && e.citizenId === user.citizenId)
      );
    }
    return [];
  }, [allEnrollments, user]);

  // Helper to refresh professors list
  const refreshProfessors = async () => {
    const res = await api.getProfessors();
    if (res.success && res.data) {
      const professors = Array.isArray(res.data) ? res.data : [];
      const processedProfessors = professors.map(prof => ({
        ...prof,
        enrollmentId: prof.id,
        status: normalizeProfessorStatus(prof.status),
      }));
      setProfessors(processedProfessors);
    }
  };

  // Helper to refresh students list
  const refreshStudents = async () => {
    const res = await api.getStudents();
    if (res.success && res.data) {
      const students = Array.isArray(res.data) ? res.data : [];
      const processedStudents = students.map(student => ({
        ...student,
        enrollmentId: student.id,
      }));
      setAllEnrollments(processedStudents);
    }
  };

  const [profRequests, setProfRequests] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_prof_requests');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  });

  const [attendanceRecords, setAttendanceRecords] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_attendance');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  });

  const [activities, setActivities] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_activities');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  });

  const [auditLogs, setAuditLogs] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_audit');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  });

  const [absenceJustifications, setAbsenceJustifications] = useState(() => {
    try {
      const saved = localStorage.getItem('lumenfall_justifications');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  });



  useEffect(() => {
    localStorage.setItem('lumenfall_prof_requests', JSON.stringify(profRequests));
  }, [profRequests]);

  useEffect(() => {
    localStorage.setItem('lumenfall_attendance', JSON.stringify(attendanceRecords));
  }, [attendanceRecords]);

  useEffect(() => {
    localStorage.setItem('lumenfall_activities', JSON.stringify(activities));
  }, [activities]);

  useEffect(() => {
    localStorage.setItem('lumenfall_audit', JSON.stringify(auditLogs));
  }, [auditLogs]);

  useEffect(() => {
    localStorage.setItem('lumenfall_justifications', JSON.stringify(absenceJustifications));
  }, [absenceJustifications]);

  // ─── Seeding seguro de contas administrativas ────────────────────────────
  // Executa apenas uma vez (controlado por ADMIN_SEED_KEY).
  // Remove a necessidade de credenciais hardcoded no código de login.
  // Credenciais padrão (trocar após primeiro acesso):
  //   Professor Admin → matrícula: admin      | senha: LF_Admin_2026!
  //   Secretaria      → matrícula: secretaria | senha: LF_Secret_2026!
  useEffect(() => {
    if (localStorage.getItem(ADMIN_SEED_KEY)) return;

    const seedAdmins = async () => {
      try {
        const [adminHash, secretHash] = await Promise.all([
          hashPassword('LF_Admin_2026!'),
          hashPassword('LF_Secret_2026!')
        ]);

        // Conta de Professor Administrador
        setProfessors(prev => {
          const hasAdmin = prev.some(p => p.enrollmentId === 'admin');
          if (hasAdmin) return prev;
          return [...prev, {
            enrollmentId: 'admin',
            fullName: 'Administrador',
            type: 'professor',
            role: 'Gestão Acadêmica',
            email: 'admin@universidade.local',
            password: adminHash,
            status: 'Ativo',
            cadastroDate: new Date().toISOString()
          }];
        });

        // Conta de Secretaria (armazenada separadamente)
        if (!localStorage.getItem('lumenfall_secretaria_account')) {
          localStorage.setItem('lumenfall_secretaria_account', JSON.stringify({
            enrollmentId: 'secretaria',
            fullName: 'Admin',
            type: 'secretaria',
            role: 'Administrador Geral',
            email: 'secretaria@universidade.local',
            password: secretHash,
            status: 'Ativo'
          }));
        }

        localStorage.setItem(ADMIN_SEED_KEY, 'true');
      } catch (err) {
        console.error('Erro ao inicializar contas administrativas:', err);
      }
    };

    seedAdmins();
  }, []);

  const addAuditLog = (action, userInfo) => {
    const log = {
      id: Date.now(),
      action,
      user: userInfo?.name || userInfo?.fullName || 'Sistema',
      userId: userInfo?.enrollmentId || userInfo?.email || 'system',
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString()
    };
    setAuditLogs(prev => [log, ...prev]);
  };

  const removeAuditLog = (id) => {
    setAuditLogs(prev => prev.filter(log => log.id !== id));
  };

  const clearAllAuditLogs = () => {
    if (window.confirm('Tem certeza que deseja apagar TODO o histórico de auditoria? Esta ação é irreversível.')) {
      setAuditLogs([]);
      return true;
    }
    return false;
  };

  const generateEnrollmentNumber = () => {
    let number;
    let isUnique = false;
    while (!isUnique) {
      number = `MAT-${Math.floor(100000 + Math.random() * 900000)}`;
      isUnique = !allEnrollments.find(e => e.enrollmentId === number);
    }
    return number;
  };

  const generateProfEnrollment = () => {
    let number;
    let isUnique = false;
    while (!isUnique) {
      number = `PROF-${Math.floor(10000 + Math.random() * 90000)}`;
      isUnique = !professors.find(p => p.enrollmentId === number);
    }
    return number;
  };

  const requestProfRegistration = (profData) => {
    const request = {
      ...profData,
      id: Date.now(),
      status: 'Pendente',
      requestDate: new Date().toISOString()
    };
    setProfRequests(prev => [...prev, request]);
    return { success: true, message: 'Solicitação enviada para a coordenação.' };
  };

  const approveProfRequest = (requestId) => {
    const request = profRequests.find(r => r.id === requestId);
    if (!request) return { success: false, message: 'Solicitação não encontrada.' };

    const enrollmentId = generateProfEnrollment();
    const newProf = {
      ...request,
      enrollmentId,
      status: 'Ativo',
      cadastroDate: new Date().toISOString(),
      type: 'professor',
      role: 'Docente'
    };

    setProfessors(prev => [...prev, newProf]);
    setProfRequests(prev => prev.filter(r => r.id !== requestId));
    
    return { success: true, data: newProf };
  };

  const addProfessorDirectly = async (profData) => {
    const enrollmentId = generateProfEnrollment();
    const newProf = {
      ...profData,
      enrollmentId,
      courses: Array.isArray(profData.courses) ? profData.courses : [profData.courses],
      status: 'pending', // New professors start as pending
      createdAt: new Date().toISOString(),
    };
    
    // Call API to create professor
    const res = await api.createProfessor({
      ...newProf,
      id: enrollmentId, // Since API uses 'id' as enrollment ID
    });
    
    if (res.success) {
      // Refresh the professors list to include new one
      await refreshProfessors();
      return { success: true, data: { ...newProf, id: enrollmentId } };
    } else {
      return { success: false, error: res.error };
    }
  };

  // Approve a pending professor
  const approveProfessor = async (professorId) => {
    const res = await api.updateProfessor(professorId, { status: 'approved' });
    if (res.success) {
      await refreshProfessors();
      return { success: true };
    }
    return { success: false, error: res.error };
  };

  // Reject a pending professor
  const rejectProfessor = async (professorId) => {
    const res = await api.updateProfessor(professorId, { status: 'rejected' });
    if (res.success) {
      await refreshProfessors();
      return { success: true };
    }
    return { success: false, error: res.error };
  };

  const addTurma = (turmaData) => {
    const newTurma = {
      ...turmaData,
      id: Date.now(),
      status: 'Ativa'
    };
    setClasses(prev => [...prev, newTurma]);
    addAuditLog(`Nova turma criada: ${turmaData.name}`, { name: 'Sistema ADM' });
    return { success: true };
  };

  const updateTurma = (id, updatedData) => {
    setClasses(prev => prev.map(c => c.id === id ? { ...c, ...updatedData } : c));
    addAuditLog(`Turma ${id} atualizada`, { name: 'Sistema ADM' });
  };

  const removeTurma = (id) => {
    setClasses(prev => prev.filter(c => c.id !== id));
    addAuditLog(`Turma ${id} removida`, { name: 'Sistema ADM' });
  };

  const addCourse = (courseData) => {
    const newCourse = {
      ...courseData,
      id: Date.now()
    };
    setCourses(prev => [...prev, newCourse]);
    addAuditLog(`Novo curso criado: ${courseData.name}`, { name: 'Sistema ADM' });
    return { success: true };
  };

  const updateCourse = (id, updatedData) => {
    setCourses(prev => prev.map(c => c.id === id ? { ...c, ...updatedData } : c));
    addAuditLog(`Curso ${id} atualizado`, { name: 'Sistema ADM' });
  };

  const removeCourse = (id) => {
    setCourses(prev => prev.filter(c => c.id !== id));
    addAuditLog(`Curso ${id} removido`, { name: 'Sistema ADM' });
  };

  const processEnrollment = async (studentData) => {
    // Suporte a múltiplos cursos (array) ou curso único (string — retrocompatibilidade)
    const courses = Array.isArray(studentData.courses) && studentData.courses.length > 0
      ? studentData.courses
      : [studentData.courseName].filter(Boolean);

    if (courses.length === 0) {
      return { success: false, message: 'Selecione pelo menos um curso para continuar.' };
    }

    const enrollmentId = generateEnrollmentNumber();
    const primaryCourse = courses[0]; // curso principal para retrocompatibilidade

    const newEnrollment = {
      ...studentData,
      courses,                    // array completo de cursos
      course: primaryCourse,      // retrocompatibilidade
      courseName: primaryCourse,  // retrocompatibilidade
      enrollmentId,
      id: enrollmentId, // API uses 'id'
      status: 'active',
      createdAt: new Date().toISOString(),
    };

    // Call API to create student
    const res = await api.createStudent(newEnrollment);
    
    if (res.success) {
      await refreshStudents();
      return { success: true, data: newEnrollment };
    } else {
      return { success: false, message: res.error || 'Erro ao criar matrícula' };
    }
  };

  const updateStudent = async (enrollmentId, updatedData) => {
    const res = await api.updateStudent(enrollmentId, updatedData);
    if (res.success) {
      setAllEnrollments(prev => prev.map(e => e.enrollmentId === enrollmentId ? { ...e, ...updatedData } : e));
      if (updatedData && Object.prototype.hasOwnProperty.call(updatedData, 'profilePicture')) {
        setIdPhotos(prev => ({ ...prev, [enrollmentId]: updatedData.profilePicture }));
      }
      addAuditLog(`Dados do aluno atualizados: ${enrollmentId}`, { name: 'Sistema' });
    }
  };

  const transferStudentTurma = async (enrollmentId, newTurma) => {
    await updateStudent(enrollmentId, { turma: newTurma });
    addAuditLog(`Aluno ${enrollmentId} transferido para Turma ${newTurma}`, { name: 'Sistema ADM' });
  };

  const updateProfessor = async (enrollmentId, updatedData) => {
    const res = await api.updateProfessor(enrollmentId, updatedData);
    if (res.success) {
      setProfessors(prev => prev.map(p => p.enrollmentId === enrollmentId ? { ...p, ...updatedData } : p));
      addAuditLog(`Dados do professor atualizados: ${enrollmentId}`, { name: 'Sistema' });
    }
  };

  const addJustification = (justification) => {
    setAbsenceJustifications(prev => [{ ...justification, id: Date.now(), status: 'Pendente' }, ...prev]);
  };

  const removeEnrollment = async (enrollmentId) => {
    const res = await api.deleteStudent(enrollmentId);
    if (res.success) {
      setAllEnrollments(prev => prev.filter(e => e.enrollmentId !== enrollmentId));
      addAuditLog(`Matrícula cancelada pelo aluno: ${enrollmentId}`, { name: 'Sistema' });
      return { success: true, message: 'Matrícula cancelada com sucesso.' };
    }
    return { success: false, message: res.error };
  };

  const removeProfessor = async (enrollmentId) => {
    const res = await api.deleteProfessor(enrollmentId);
    if (res.success) {
      setProfessors(prev => prev.filter(p => p.enrollmentId !== enrollmentId));
      addAuditLog(`Professor removido: ${enrollmentId}`, { name: 'Sistema ADM' });
      return { success: true, message: 'Registro docente removido com sucesso.' };
    }
    return { success: false, message: res.error };
  };

  const calculateFrequency = (studentId, courseName, turma) => {
    const classId = `${courseName}-${turma}`;
    const relevantRecords = attendanceRecords.filter(r => r.classId === classId || r.classId === `${courseName}-${turma}`);
    
    if (relevantRecords.length === 0) return 100;

    let presentCount = 0;
    let totalCount = 0;

    relevantRecords.forEach(record => {
      const status = record.attendance[studentId];
      if (status) {
        totalCount++;
        if (status === 'P' || status === 'A' || status === 'J') {
          presentCount++;
        }
      }
    });

    return totalCount === 0 ? 100 : Math.round((presentCount / totalCount) * 100);
  };

  const calculateClassFrequency = (courseName, turma) => {
    const classId = `${courseName}-${turma}`;
    const relevantRecords = attendanceRecords.filter(r => r.classId === classId);
    
    if (relevantRecords.length === 0) return 100;

    let totalPossible = 0;
    let totalPresent = 0;

    relevantRecords.forEach(record => {
      Object.values(record.attendance).forEach(status => {
        totalPossible++;
        if (status === 'P' || status === 'A' || status === 'J') {
          totalPresent++;
        }
      });
    });

    return totalPossible === 0 ? 100 : Math.round((totalPresent / totalPossible) * 100);
  };

  return (
    <DataContext.Provider value={{ 
      notices, addNotice, removeNotice, 
      notifications, markAllAsRead, markNotificationAsRead, readNotices,
      classes, updateFrequency,
      grades, updateGrade, deleteGradesByAssessment,
      assessments, setAssessments,
      idPhotos,
      chatMessages,
      getConversationId,
      getConversationMessages,
      sendChatMessage,
      studentEnrollments,
      absenceJustifications, addJustification,
      allEnrollments, processEnrollment,
      removeEnrollment, removeProfessor,
      professors,
      profRequests,
      requestProfRegistration,
      approveProfRequest,
      addProfessorDirectly,
      updateProfessor,
      approveProfessor,
      rejectProfessor,
      refreshProfessors,
      refreshStudents,
      attendanceRecords,
      registerAttendance,
      calculateFrequency,
      calculateClassFrequency,
      auditLogs,
      addAuditLog,
      updateStudent,
      transferStudentTurma,
      activities,
      addTurma,
      updateTurma,
      removeTurma,
      courses,
      addCourse,
      updateCourse,
      removeCourse,
      removeAuditLog,
      clearAllAuditLogs,
      loadingData,
    }}>
      {children}
    </DataContext.Provider>
  );
};
